import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Phone, MapPin, Send, ChevronDown } from "lucide-react";
import { SITE } from "@/constants/testIds";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PHONE = "541-890-3374";
const PHONE_TEL = "+15418903374";

const experienceOptions = [
  { value: "", label: "Select experience level" },
  { value: "beginner", label: "Beginner — never or barely cast" },
  { value: "intermediate", label: "Intermediate — some casting experience" },
];

const initial = {
  name: "",
  email: "",
  phone: "",
  experience_level: "",
  group_size: 1,
  contact_preference: "email",
  message: "",
};

function formatApiErrorDetail(detail) {
  if (detail == null) return null;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export default function Contact() {
  const [form, setForm] = useState(initial);
  const [submitting, setSubmitting] = useState(false);

  const update = (k) => (e) =>
    setForm((f) => ({
      ...f,
      [k]: k === "group_size" ? e.target.value : e.target.value,
    }));

  const submit = async (e) => {
    e.preventDefault();
    if (
      !form.name.trim() ||
      !form.email.trim() ||
      !form.phone.trim() ||
      !form.experience_level ||
      !form.group_size ||
      !form.contact_preference
    ) {
      toast.error("Please fill in all required fields.");
      return;
    }
    const groupSize = Number(form.group_size);
    if (!Number.isInteger(groupSize) || groupSize < 1 || groupSize > 20) {
      toast.error("Group size must be between 1 and 20.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/bookings`, {
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        experience_level: form.experience_level,
        group_size: groupSize,
        contact_preference: form.contact_preference,
        message: form.message.trim() || null,
      });
      toast.success(
        "Thank you! Mike will reach out shortly to schedule your session."
      );
      setForm(initial);
    } catch (err) {
      const msg =
        formatApiErrorDetail(err?.response?.data?.detail) ||
        err?.message ||
        "Something went wrong. Please try again or call directly.";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid={SITE.contact}
      className="relative bg-brand-evergreen py-24 text-brand-cream md:py-32"
    >
      <div className="mx-auto max-w-7xl px-6 md:px-10">
        <div className="grid gap-14 md:grid-cols-12 md:gap-20">
          {/* Left column — Headline + info */}
          <div className="md:col-span-5">
            <span className="text-xs uppercase tracking-[0.35em] text-brand-sand">
              Contact
            </span>
            <h2 className="mt-3 font-display text-5xl leading-tight text-brand-cream sm:text-6xl">
              Let&rsquo;s go{" "}
              <span className="italic text-brand-sand">casting.</span>
            </h2>
            <p className="mt-6 max-w-md text-base leading-relaxed text-brand-cream/80">
              Reach out by phone, text, or the form. We&rsquo;ll find a mutual
              time and location that works.
            </p>

            <div className="mt-10 space-y-5 text-sm">
              <a
                data-testid={SITE.contactPhoneLink}
                href={`tel:${PHONE_TEL}`}
                className="group flex items-start gap-4"
              >
                <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-brand-cream/10 text-brand-sand transition-colors group-hover:bg-brand-sand group-hover:text-brand-evergreen">
                  <Phone className="h-5 w-5" />
                </span>
                <span>
                  <span className="block text-xs uppercase tracking-[0.25em] text-brand-cream/60">
                    Phone or text
                  </span>
                  <span className="block text-lg text-brand-cream group-hover:text-brand-sand">
                    {PHONE}
                  </span>
                </span>
              </a>

              <div className="flex items-start gap-4">
                <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-brand-cream/10 text-brand-sand">
                  <MapPin className="h-5 w-5" />
                </span>
                <span>
                  <span className="block text-xs uppercase tracking-[0.25em] text-brand-cream/60">
                    Where we cast
                  </span>
                  <span className="block text-lg text-brand-cream">
                    Lessons are held at selected parks and river access
                    locations in the Grants Pass, Medford, Rogue River, and
                    greater Rogue Valley area.
                  </span>
                </span>
              </div>
            </div>

            <div className="mt-10 overflow-hidden rounded-2xl ring-1 ring-brand-cream/15">
              <iframe
                title="Map of Rogue Valley, Southern Oregon"
                src="https://www.google.com/maps?q=Rogue+Valley,+Oregon&output=embed"
                width="100%"
                height="240"
                style={{ border: 0 }}
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          </div>

          {/* Right column — Request a Session form */}
          <div className="md:col-span-7">
            <form
              onSubmit={submit}
              data-testid={SITE.contactForm}
              className="rounded-3xl bg-brand-cream p-8 text-brand-charcoal shadow-xl ring-1 ring-brand-cream/10 md:p-12"
            >
              <h3 className="font-display text-3xl text-brand-evergreen">
                Request a Session
              </h3>
              <p className="mt-2 text-sm text-brand-charcoal/60">
                Share a few details, your contact preference, and Mike will
                reach out to schedule your session.
              </p>

              <div className="mt-8 grid gap-5 sm:grid-cols-2">
                <label className="block">
                  <span className="text-xs uppercase tracking-[0.2em] text-brand-evergreen/70">
                    Name
                  </span>
                  <input
                    data-testid={SITE.contactName}
                    type="text"
                    value={form.name}
                    onChange={update("name")}
                    required
                    autoComplete="name"
                    className="mt-2 w-full rounded-lg border border-brand-evergreen/15 bg-white px-4 py-3 text-base text-brand-charcoal outline-none transition focus:border-brand-evergreen focus:ring-2 focus:ring-brand-evergreen/15"
                  />
                </label>
                <label className="block">
                  <span className="text-xs uppercase tracking-[0.2em] text-brand-evergreen/70">
                    Email
                  </span>
                  <input
                    data-testid={SITE.contactEmail}
                    type="email"
                    value={form.email}
                    onChange={update("email")}
                    required
                    autoComplete="email"
                    className="mt-2 w-full rounded-lg border border-brand-evergreen/15 bg-white px-4 py-3 text-base text-brand-charcoal outline-none transition focus:border-brand-evergreen focus:ring-2 focus:ring-brand-evergreen/15"
                  />
                </label>
              </div>

              <div className="mt-5 grid gap-5 sm:grid-cols-2">
                <label className="block">
                  <span className="text-xs uppercase tracking-[0.2em] text-brand-evergreen/70">
                    Phone number
                  </span>
                  <input
                    data-testid={SITE.contactPhone}
                    type="tel"
                    value={form.phone}
                    onChange={update("phone")}
                    required
                    autoComplete="tel"
                    className="mt-2 w-full rounded-lg border border-brand-evergreen/15 bg-white px-4 py-3 text-base text-brand-charcoal outline-none transition focus:border-brand-evergreen focus:ring-2 focus:ring-brand-evergreen/15"
                  />
                </label>
                <label className="block">
                  <span className="text-xs uppercase tracking-[0.2em] text-brand-evergreen/70">
                    Group size
                  </span>
                  <input
                    data-testid={SITE.contactGroupSize}
                    type="number"
                    inputMode="numeric"
                    min={1}
                    max={20}
                    value={form.group_size}
                    onChange={update("group_size")}
                    required
                    className="mt-2 w-full rounded-lg border border-brand-evergreen/15 bg-white px-4 py-3 text-base text-brand-charcoal outline-none transition focus:border-brand-evergreen focus:ring-2 focus:ring-brand-evergreen/15"
                  />
                </label>
              </div>

              <label className="mt-5 block">
                <span className="text-xs uppercase tracking-[0.2em] text-brand-evergreen/70">
                  Experience level
                </span>
                <div className="relative mt-2">
                  <select
                    data-testid={SITE.contactExperience}
                    value={form.experience_level}
                    onChange={update("experience_level")}
                    required
                    className="w-full appearance-none rounded-lg border border-brand-evergreen/15 bg-white px-4 py-3 pr-10 text-base text-brand-charcoal outline-none transition focus:border-brand-evergreen focus:ring-2 focus:ring-brand-evergreen/15"
                  >
                    {experienceOptions.map((opt) => (
                      <option
                        key={opt.value}
                        value={opt.value}
                        disabled={opt.value === ""}
                      >
                        {opt.label}
                      </option>
                    ))}
                  </select>
                  <ChevronDown className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-brand-evergreen/50" />
                </div>
              </label>

              <fieldset className="mt-5">
                <legend className="text-xs uppercase tracking-[0.2em] text-brand-evergreen/70">
                  Preferred contact method
                </legend>
                <div className="mt-2 grid grid-cols-3 gap-3">
                  {[
                    { value: "email", label: "Email" },
                    { value: "phone", label: "Phone" },
                    { value: "text", label: "Text" },
                  ].map((opt) => {
                    const selected = form.contact_preference === opt.value;
                    return (
                      <label
                        key={opt.value}
                        data-testid={`contact-input-preference-${opt.value}`}
                        className={`flex cursor-pointer items-center justify-center rounded-lg border px-4 py-3 text-sm font-medium transition ${
                          selected
                            ? "border-brand-evergreen bg-brand-evergreen text-brand-cream"
                            : "border-brand-evergreen/15 bg-white text-brand-charcoal hover:border-brand-evergreen/40"
                        }`}
                      >
                        <input
                          type="radio"
                          name="contact_preference"
                          value={opt.value}
                          checked={selected}
                          onChange={update("contact_preference")}
                          className="sr-only"
                        />
                        {opt.label}
                      </label>
                    );
                  })}
                </div>
              </fieldset>

              <label className="mt-5 block">
                <span className="text-xs uppercase tracking-[0.2em] text-brand-evergreen/70">
                  Message to Mike
                </span>
                <textarea
                  data-testid={SITE.contactMessage}
                  value={form.message}
                  onChange={update("message")}
                  rows={5}
                  className="mt-2 w-full resize-none rounded-lg border border-brand-evergreen/15 bg-white px-4 py-3 text-base text-brand-charcoal outline-none transition focus:border-brand-evergreen focus:ring-2 focus:ring-brand-evergreen/15"
                />
              </label>

              <button
                type="submit"
                data-testid={SITE.contactSubmit}
                disabled={submitting}
                className="mt-8 inline-flex w-full items-center justify-center gap-2 rounded-full bg-brand-evergreen px-7 py-4 text-sm font-medium tracking-wide text-brand-cream transition-all duration-300 hover:bg-brand-sand hover:text-brand-evergreen disabled:opacity-60 sm:w-auto"
              >
                <Send className="h-4 w-4" />
                {submitting ? "Sending..." : "Request a Session"}
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
